import React, { useState, useMemo } from 'react';
import { GenAIWrapper } from 'shared/security/GenAIWrapperProvider';
import { ProtectedVisibility } from 'shared/security/ProtectedRoute';
import apiCall from 'shared/services/ApiAdapter';
import genAIApiCall from 'shared/services/GenAPIPath';
import { getCurrentUserId } from 'shared/utils/SessionDataUtils';

// ==========================================
// 1. Icons & UI Helpers
// ==========================================

const Icons = {
    Sparkles: () => (
        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"></path></svg>
    ),
    Chart: () => (
        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 002 2h2a2 2 0 002-2z"></path></svg>
    ),
    Check: () => (
        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>
    ),
    Close: () => (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
    ),
    TrendingUp: () => (
        <svg className="w-5 h-5 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"></path></svg>
    ),
    TrendingDown: () => (
        <svg className="w-5 h-5 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6"></path></svg>
    ),
    Brain: () => (
        <svg className="w-6 h-6 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"></path></svg>
    )
};

// ==========================================
// 2. Workflow Result Modal
// ==========================================

const WorkflowResultModal = ({ isOpen, onClose, onMerge, data }) => {
    const parsedResult = useMemo(() => {
        if (!data || !data.result) return null;
        try {
            return typeof data.result === 'string' ? JSON.parse(data.result) : data.result;
        } catch (error) {
            console.error("Failed to parse workflow result:", error);
            return null;
        }
    }, [data]);

    if (!isOpen) return null;

    const hasResults = parsedResult && Object.keys(parsedResult).length > 0;

    const renderSection = (title, icon, content) => {
        if (!content) return null;
        let textContent = content;
        if (typeof content === 'object') {
            textContent = Object.values(content).join(". ");
        }

        return (
            <div className="bg-slate-50 dark:bg-slate-900/50 p-4 rounded-lg border border-slate-200 dark:border-slate-700 mb-4">
                <div className="flex items-center gap-2 mb-2">
                    {icon}
                    <h4 className="font-semibold text-slate-800 dark:text-slate-200 text-sm uppercase tracking-wide">{title}</h4>
                </div>
                <div className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed whitespace-pre-wrap">
                    {String(textContent)}
                </div>
            </div>
        );
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm transition-opacity">
            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-4xl w-full flex flex-col max-h-[90vh] border border-slate-200 dark:border-slate-700 overflow-hidden">
                <div className="bg-slate-100 dark:bg-slate-900/80 px-8 py-5 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-white dark:bg-slate-800 rounded-full shadow-sm">
                            <Icons.Brain />
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-slate-900 dark:text-white">Assignment Analysis Report</h3>
                            <p className="text-xs text-slate-500 dark:text-slate-400">Automated workflow evaluation</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors">
                        <Icons.Close />
                    </button>
                </div>

                <div className="p-8 overflow-y-auto flex-1 bg-white dark:bg-slate-800">
                    {!hasResults ? (
                        <div className="text-center py-12">
                            <p className="text-slate-500 mb-4">No structured analysis found.</p>
                            <pre className="text-xs bg-slate-100 dark:bg-slate-950 p-4 rounded-lg text-left overflow-auto max-h-64 border border-slate-200">
                                {JSON.stringify(data, null, 2)}
                            </pre>
                        </div>
                    ) : (
                        <div className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <div className="md:col-span-1 bg-slate-900 text-white rounded-xl p-6 flex flex-col items-center justify-center text-center shadow-lg">
                                    <span className="text-slate-400 text-xs font-bold uppercase tracking-widest mb-2">Calculated Score</span>
                                    <span className="text-6xl font-black tracking-tighter">
                                        {parsedResult.rate || parsedResult.score || "-"}
                                    </span>
                                    <span className="text-slate-400 text-xs mt-2">out of 10</span>
                                </div>

                                <div className="md:col-span-2 flex flex-col">
                                    {renderSection("Executive Summary", <Icons.Chart />, parsedResult.overallFeedback || parsedResult.reasoning)}

                                    {parsedResult.suggestedTopics && (
                                        <div className="mt-auto">
                                            <h5 className="text-xs font-bold text-slate-500 uppercase mb-2">Recommended Topics</h5>
                                            <div className="flex flex-wrap gap-2">
                                                {parsedResult.suggestedTopics.map((topic, i) => (
                                                    <span key={i} className="px-3 py-1 bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300 rounded-full text-xs font-medium border border-indigo-100 dark:border-indigo-800">
                                                        {topic}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <hr className="border-slate-100 dark:border-slate-700" />
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>{renderSection("Key Strengths", <Icons.TrendingUp />, parsedResult.strengthAreas)}</div>
                                <div>{renderSection("Areas for Improvement", <Icons.TrendingDown />, parsedResult.improvementAreas)}</div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="p-3 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 flex justify-between items-center">
                    <span className="py-1 text-xs text-slate-400 hidden sm:block">* GenAI generated content, verify details before merging.
                        <div>Reasoning: {parsedResult.reasoning}</div></span>
                    <div className="flex space-x-3 ml-auto">
                        <button
                            onClick={onClose}
                            className="px-5 py-2.5 text-sm font-semibold text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 hover:text-slate-800 shadow-sm transition-all focus:ring-2 focus:ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-600 dark:hover:bg-slate-700"
                        >
                            Dismiss
                        </button>
                        <button
                            onClick={() => onMerge(parsedResult)}
                            disabled={!hasResults}
                            className={`px-5 py-2.5 text-sm font-semibold text-white rounded-lg shadow-md transition-all flex items-center
                                ${hasResults
                                    ? 'bg-slate-900 hover:bg-slate-800 hover:shadow-lg focus:ring-2 focus:ring-slate-400 dark:bg-indigo-600 dark:hover:bg-indigo-500'
                                    : 'bg-slate-400 cursor-not-allowed opacity-70'
                                }`}
                        >
                            <Icons.Check />
                            Merge Results
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ==========================================
// 3. Main Form Component
// ==========================================

const AssignmentFeedbackForm = ({
    assignment,
    questions,
    startLoading,
    stopLoading,
    displayToast,
    onListView,
}) => {
    const userId = getCurrentUserId();
    const [formData, setFormData] = useState({
        feedback: assignment?.feedback ?? "",
        score: assignment?.score ?? "",
        strengthAreas: assignment?.strengthAreas ?? "",
        improvementAreas: assignment?.improvementAreas ?? "",
        status: "Completed",
        updatedBy: userId,
        updatedDate: new Date().toISOString(),
    });

    const [errors, setErrors] = useState({});
    const [showWorkflowModal, setShowWorkflowModal] = useState(false);
    const [workflowResult, setWorkflowResult] = useState(null);

    const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

    const handleFetchAndMergeMetrics = async () => {
        if (!assignment || !assignment.id) {
            console.error("Assignment ID is missing");
            displayToast({ type: "error", message: "Assignment data is invalid." });
            return;
        }

        try {
            startLoading();

            const metricsData = await apiCall(
                `/question-metrics?assignmentId.equals=${assignment.id}&isActive.equals=true`,
                false,
                "GET",
                null,
                () => { },
                () => { },
                () => { }
            );


            const safeMetrics = Array.isArray(metricsData) ? metricsData : [];

            const questionsWithMetrics = (questions || []).map((question) => {
                const matchingMetric = safeMetrics.find((m) => m.questionCode === question.code);
                return { ...question, questionMetric: matchingMetric || null };
            });

            const submitData = await genAIApiCall("/workflows/submit/assessment-analyzer-workflow",
                'POST', JSON.stringify(questionsWithMetrics), startLoading, stopLoading, displayToast);

            if (!submitData.jobId) {
                throw new Error("No Job ID returned");
            }

            displayToast("AI Analysis in progress. This may take up to a minute...");

            let resultData = null;
            for (let i = 0; i < 20; i++) {
                await delay(3000);
                // try {
                const statusData = await genAIApiCall(`/workflows/status/${submitData.jobId}`,
                    'GET', null, startLoading, stopLoading, displayToast);

                if (statusData && statusData.status === 'FAILED') {
                    console.log(statusData.result);
                    throw new Error("Failed to execute due to quota or model not found");
                }
                if (statusData && statusData.status === 'COMPLETED') {
                    resultData = statusData;
                    break;
                }
                // } catch (e) {
                //     console.warn("Polling attempt failed", e);
                //     break;
                // }
            }
            // displayToast("AI Analysis completed...");

            if (resultData) {
                setWorkflowResult(resultData);
                setShowWorkflowModal(true);
            } else {
                throw new Error("Polling timed out without completion");
            }

        } catch (err) {
            console.error("AI Generation Error:", err);
            displayToast({ type: "error", message: "AI analysis cannot be generated at this time." });
        } finally {
            stopLoading();
        }
    };

    const handleModalMerge = (parsedData) => {
        if (!parsedData) return;

        let formattedFeedback = parsedData.overallFeedback || parsedData.reasoning || "";
        if (parsedData.suggestedTopics && Array.isArray(parsedData.suggestedTopics)) {
            formattedFeedback += "\n\nSuggested Topics:\n" + parsedData.suggestedTopics.map(t => `- ${t}`).join("\n");
        }

        let formattedStrengths = "";
        if (typeof parsedData.strengthAreas === 'object' && parsedData.strengthAreas !== null) {
            formattedStrengths = Object.values(parsedData.strengthAreas).join(". ");
        } else {
            formattedStrengths = parsedData.strengthAreas || "";
        }

        let formattedImprovements = "";
        if (typeof parsedData.improvementAreas === 'object' && parsedData.improvementAreas !== null) {
            const parts = [];
            if (parsedData.improvementAreas.improvementRequired) parts.push(`Improvement: ${parsedData.improvementAreas.improvementRequired}`);
            if (parsedData.improvementAreas.suggestedAction) parts.push(`Action: ${parsedData.improvementAreas.suggestedAction}`);
            formattedImprovements = parts.length > 0 ? parts.join("\n") : Object.values(parsedData.improvementAreas).join(". ");
        } else {
            formattedImprovements = parsedData.improvementAreas || "";
        }

        // --- UPDATE STATE: DO NOT OVERWRITE SCORE ---
        setFormData(prev => ({
            ...prev,
            // score: parsedData.rate ... (REMOVED: Keeping original score)
            feedback: formattedFeedback,
            strengthAreas: formattedStrengths,
            improvementAreas: formattedImprovements,
        }));

        setShowWorkflowModal(false);
        displayToast("Results merged successfully");
    };

    const validate = () => {
        const newErrors = {};
        if (formData.score !== "" && isNaN(formData.score)) newErrors.score = "Must be a number";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        try {
            const payload = { ...assignment, ...formData, updatedDate: new Date().toISOString() };
            await apiCall(`/assignments/${assignment.id}`, false, "PATCH", payload, startLoading, stopLoading, () => { });
            displayToast("Assignment updated successfully");
            onListView(payload);
        } catch (error) {
            displayToast("Failed to update assignment");
        }
    };

    return (
        <div className="min-h-screen w-full py-2 px-2 sm:px-6 relative font-sans text-slate-800 dark:text-slate-200">

            <WorkflowResultModal
                isOpen={showWorkflowModal}
                onClose={() => setShowWorkflowModal(false)}
                onMerge={handleModalMerge}
                data={workflowResult}
            />

            <form onSubmit={handleSubmit} className="max-w-8xl w-full mx-auto">
                {/* Header Section */}
                <div className="mb-2 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">
                            Assignment Review
                        </h1>
                        <p className="mt-2 text-slate-500 dark:text-slate-400">
                            ID: <span className="font-mono text-slate-700 dark:text-slate-300">{assignment?.id}</span> • Student Feedback & Grading
                        </p>
                    </div>
                    <GenAIWrapper>
                        <ProtectedVisibility requiredRoles={["ROLE_TEACHER"]} beta={true}>
                            {/* Added 'relative' and 'inline-flex' to the wrapper div */}
                            <div className="relative inline-flex group">

                                {/* Workflow Trigger Button */}
                                <button
                                    type="button"
                                    onClick={handleFetchAndMergeMetrics}
                                    // Added 'pr-8' (padding-right) to make space for the tag so text doesn't overlap
                                    className="inline-flex items-center px-5 py-2.5 bg-white border border-slate-300 text-slate-700 text-sm font-semibold rounded-lg hover:border-indigo-400 hover:text-indigo-600 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all dark:bg-slate-800 dark:text-slate-200 dark:border-slate-600 dark:hover:border-indigo-500"
                                >
                                    <span className="p-1 bg-indigo-50 rounded text-indigo-600 mr-2 group-hover:bg-indigo-100 transition-colors dark:bg-slate-700 dark:text-indigo-400">
                                        <Icons.Sparkles />
                                    </span>
                                    <span>Auto-Generate with AI</span>
                                </button>

                                {/* Beta Badge */}
                                <span
                                    className="absolute -top-3 -right-3 px-2 py-0.5 text-[10px] font-bold tracking-wide
 text-gray-800 bg-gray-100 rounded-full dark:bg-gray-700 dark:text-gray-300
        shadow-sm transform rotate-12 z-10"
                                >
                                    beta
                                </span>
                            </div>
                        </ProtectedVisibility>
                    </GenAIWrapper>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">

                    {/* Main Content Column (Left) - Widened to 9 columns (75%) */}
                    <div className="lg:col-span-9">

                        {/* Unified Card Container */}
                        <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 divide-y divide-slate-100 dark:divide-slate-800">

                            {/* ROW 1: General Feedback */}
                            <div className="p-4 grid grid-cols-1 lg:grid-cols-12 gap-6">
                                {/* Left Column: Label */}
                                <div className="lg:col-span-2">
                                    <label className="text-sm font-bold text-slate-900 dark:text-white block mb-1">
                                        Tutor Feedback
                                    </label>
                                    <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                                        General assessment summary and overall thoughts on the student's submission.
                                    </p>
                                </div>

                                {/* Right Column: Input */}
                                <div className="lg:col-span-10">
                                    <textarea
                                        name="feedback"
                                        value={formData.feedback}
                                        onChange={(e) => setFormData({ ...formData, feedback: e.target.value })}
                                        rows={6}
                                        className="block w-full rounded-lg border-slate-300 bg-slate-50 focus:bg-white focus:border-indigo-500 focus:ring-indigo-500 transition-colors dark:bg-slate-950 dark:border-slate-700 dark:text-slate-100 p-3 text-sm leading-relaxed"
                                        placeholder="Enter general feedback..."
                                    />
                                </div>
                            </div>

                            {/* ROW 2: Strengths */}
                            <div className="p-4 grid grid-cols-1 lg:grid-cols-12 gap-6">
                                <div className="lg:col-span-2">
                                    <div className="flex items-center gap-2 mb-1 text-emerald-600 dark:text-emerald-500">
                                        <label className="text-sm font-bold text-slate-900 dark:text-white">
                                            Strength Areas
                                        </label>
                                    </div>
                                    <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                                        Specific concepts or answers where the student demonstrated strong understanding.
                                    </p>
                                </div>

                                <div className="lg:col-span-10">
                                    <textarea
                                        name="strengthAreas"
                                        value={formData.strengthAreas}
                                        onChange={(e) => setFormData({ ...formData, strengthAreas: e.target.value })}
                                        rows={4}
                                        className="block w-full rounded-lg border-slate-300 bg-slate-50 focus:bg-white focus:border-emerald-500 focus:ring-emerald-500 transition-colors dark:bg-slate-950 dark:border-slate-700 dark:text-slate-100 p-3 text-sm leading-relaxed"
                                        placeholder="List key strengths..."
                                    />
                                </div>
                            </div>

                            {/* ROW 3: Improvements */}
                            <div className="p-4 grid grid-cols-1 lg:grid-cols-12 gap-6">
                                <div className="lg:col-span-2">
                                    <div className="flex items-center gap-2 mb-1 text-amber-600 dark:text-amber-500">
                                        <label className="text-sm font-bold text-slate-900 dark:text-white">
                                            Areas for Improvement
                                        </label>
                                    </div>
                                    <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                                        Topics requiring further review or answers that were partially incorrect.
                                    </p>
                                </div>

                                <div className="lg:col-span-10">
                                    <textarea
                                        name="improvementAreas"
                                        value={formData.improvementAreas}
                                        onChange={(e) => setFormData({ ...formData, improvementAreas: e.target.value })}
                                        rows={4}
                                        className="block w-full rounded-lg border-slate-300 bg-slate-50 focus:bg-white focus:border-amber-500 focus:ring-amber-500 transition-colors dark:bg-slate-950 dark:border-slate-700 dark:text-slate-100 p-3 text-sm leading-relaxed"
                                        placeholder="List areas for improvement..."
                                    />
                                </div>
                            </div>

                        </div>
                    </div>

                    {/* Sidebar Column (Right) - Narrowed to 3 columns (25%) */}
                    <div className="lg:col-span-3 space-y-6">

                        {/* Score Card */}
                        <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 p-6">
                            <label className="block text-sm font-bold text-slate-900 dark:text-white mb-2 uppercase tracking-wide">
                                Final Score
                            </label>
                            <div className="relative">
                                <input
                                    type="number"
                                    name="score"
                                    value={formData.score}
                                    onChange={(e) => setFormData({ ...formData, score: e.target.value })}
                                    className="block w-full text-5xl font-black text-slate-800 bg-transparent border-0 border-b-2 border-slate-200 focus:border-slate-900 focus:ring-0 px-0 py-4 placeholder-slate-200 dark:text-white dark:border-slate-700 dark:focus:border-white"
                                    placeholder="00"
                                />
                                <span className="absolute right-0 bottom-4 text-slate-400 font-medium">/ {questions.length}</span>
                            </div>
                            {errors.score && <p className="text-red-500 text-xs mt-2 font-medium">{errors.score}</p>}
                        </div>

                        {/* Status/Metadata Card */}
                        <div className="bg-slate-50 dark:bg-slate-900/50 rounded-xl border border-slate-200 dark:border-slate-800 p-6">
                            <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-4">Metadata</h4>

                            <div className="space-y-4">
                                <div>
                                    <span className="text-xs text-slate-400 block">Status</span>
                                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-800 px-3 py-1 rounded border border-slate-200 dark:border-slate-700 inline-block mt-1">
                                        {formData.status}
                                    </span>
                                </div>
                                <div>
                                    <span className="text-xs text-slate-400 block">Last Updated</span>
                                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                        {new Date().toLocaleDateString()}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="pt-4">
                            <button
                                type="submit"
                                className="w-full flex justify-center items-center px-6 py-4 bg-slate-900 text-white text-base font-bold rounded-xl hover:bg-slate-800 focus:outline-none focus:ring-4 focus:ring-slate-300 transition-all shadow-lg dark:bg-indigo-600 dark:hover:bg-indigo-500 dark:focus:ring-indigo-900"
                            >
                                <Icons.Check />
                                Submit
                            </button>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default AssignmentFeedbackForm;